import DOMPurify from 'dompurify';
import type { Point, CategorieMarqueur } from '../../types/map';
import { resolveAssetUrl } from '../../config/api';

/**
 * Configuration DOMPurify — limite les balises autorisées
 * pour éviter les injections XSS tout en permettant un HTML riche
 */
const SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    'div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'strong', 'em', 'b', 'i', 'u', 'br', 'hr',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'img', 'a',
    'small', 'sub', 'sup', 'blockquote', 'pre', 'code',
    'dl', 'dt', 'dd', 'figure', 'figcaption',
  ],
  ALLOWED_ATTR: [
    'class', 'id', 'style',
    'src', 'alt', 'title', 'width', 'height',
    'href', 'target', 'rel',
    'colspan', 'rowspan',
    'data-field',
  ],
  ALLOW_DATA_ATTR: false,
};

/**
 * Variables système disponibles dans tous les templates
 */
const SYSTEM_VARIABLES = [
  'nom', 'titre', 'description', 'categorie',
  'latitude', 'longitude', 'couleur',
] as const;

/**
 * Résultat de `buildVariables` : les variables scalaires substituables directement
 * via {{cle}}, et les tableaux (métadonnées de type 'galerie' ou toute future
 * métadonnée "liste") consommables via la boucle générique {{#each cle}}.
 */
export interface TemplateData {
  vars: Record<string, string>;
  arrays: Record<string, Array<Record<string, string>>>;
}

/**
 * Résout une valeur en chaîne, en transformant les chemins relatifs
 * (ex. /uploads/...) en URL absolue via `resolveAssetUrl`. Les autres
 * valeurs (texte libre, nombres, etc.) restent inchangées.
 */
function resolveIfRelative(value: unknown): string {
  const s = String(value ?? '');
  return s.startsWith('/') ? resolveAssetUrl(s) : s;
}

/**
 * Aplatit un élément de tableau (ex. une image de galerie `{url, alt, legende}`)
 * en un dictionnaire de chaînes exploitable par {{#each}}. Générique : n'importe
 * quelle forme d'objet est supportée, chaque champ propre devient un token {{champ}}.
 */
function flattenArrayItem(item: unknown): Record<string, string> {
  if (typeof item !== 'object' || item === null) {
    return { value: resolveIfRelative(item) };
  }
  return Object.fromEntries(
    Object.entries(item as Record<string, unknown>).map(([k, v]) => [k, resolveIfRelative(v)])
  );
}

/**
 * Construit le dictionnaire de variables à partir du point et de sa catégorie.
 * Les métadonnées dont la valeur est un tableau (type 'galerie') sont exposées
 * séparément dans `arrays`, pour être consommées par la boucle {{#each cle}} ;
 * les autres métadonnées sont aplaties en chaînes dans `vars`.
 */
export function buildVariables(
  point: Point,
  categorie: CategorieMarqueur
): TemplateData {
  const vars: Record<string, string> = {
    nom: point.nom || '',
    titre: point.titre || point.nom || '',
    description: (point.description || '').replace(/\n/g, '<br>'),
    categorie: categorie.nom || '',
    latitude: String(point.latitude ?? ''),
    longitude: String(point.longitude ?? ''),
    couleur: categorie.couleur || point.styleIcone?.couleur || '#FF5733',
  };
  const arrays: Record<string, Array<Record<string, string>>> = {};

  for (const [cle, valeur] of Object.entries(point.donneesPersonnalisees || {})) {
    if (Array.isArray(valeur)) {
      arrays[cle] = valeur.map(flattenArrayItem);
    } else {
      vars[cle] = resolveIfRelative(valeur);
    }
  }

  return { vars, arrays };
}

/**
 * Teste si une valeur de variable est "truthy" pour les blocs conditionnels.
 * Falsy : undefined, chaîne vide, "non", "false"
 */
function isTruthy(value: string | undefined): boolean {
  if (value === undefined || value === '') return false;
  const lower = value.toLowerCase();
  return lower !== 'non' && lower !== 'false';
}

/**
 * Traite les blocs conditionnels {{#if variable}}...{{else}}...{{/if}}
 * dans le template HTML. Résout les blocs imbriqués par passes successives
 * (du plus interne au plus externe).
 *
 * `arrays` (optionnel) permet de tester la "vérité" d'une variable liée à un
 * tableau (ex. métadonnée 'galerie') : {{#if cle}} est vrai si le tableau
 * existe et contient au moins un élément.
 */
export function processConditionals(
  html: string,
  vars: Record<string, string>,
  arrays: Record<string, Array<Record<string, string>>> = {}
): string {
  // Regex non-gourmande qui capture le bloc if le plus interne
  // (pas de {{#if imbriqué dans le contenu capturé)
  const IF_REGEX = /\{\{#if\s+(\w+)\}\}((?:(?!\{\{#if\s)(?!\{\{\/if\}\})[\s\S])*?)(?:\{\{else\}\}((?:(?!\{\{#if\s)(?!\{\{\/if\}\})[\s\S])*?))?\{\{\/if\}\}/;

  let result = html;
  let safety = 0;
  while (IF_REGEX.test(result) && safety < 100) {
    result = result.replace(IF_REGEX, (_, varName: string, ifBlock: string, elseBlock?: string) => {
      const truthy = varName in vars ? isTruthy(vars[varName]) : (arrays[varName]?.length ?? 0) > 0;
      return truthy ? ifBlock : (elseBlock ?? '');
    });
    safety++;
  }
  return result;
}

/**
 * Traite les boucles génériques {{#each cle}}...{{/each}}. `cle` doit référencer
 * un tableau exposé dans `arrays` (typiquement une métadonnée 'galerie') ; chaque
 * itération expose les champs propres de l'élément (ex. {{url}}, {{alt}}) ainsi
 * qu'un token intrinsèque {{index}} (position 0-based). Non générique au concept
 * de "galerie" : fonctionne avec n'importe quelle métadonnée de type tableau.
 *
 * ⚠️ Contrepartie mirroir : garder synchronisé avec `processEach` dans
 * `backoffice/src/components/templateEditor/TemplatePreview.tsx`.
 */
export function processEach(
  html: string,
  vars: Record<string, string>,
  arrays: Record<string, Array<Record<string, string>>>
): string {
  const EACH_REGEX = /\{\{#each\s+(\w+)\}\}([\s\S]*?)\{\{\/each\}\}/;

  let result = html;
  let safety = 0;
  while (EACH_REGEX.test(result) && safety < 50) {
    result = result.replace(EACH_REGEX, (_, cle: string, block: string) => {
      const items = arrays[cle] ?? [];
      return items
        .map((item, index) => {
          const itemVars: Record<string, string> = { ...vars, ...item, index: String(index) };
          const withConditionals = processConditionals(block, itemVars);
          return withConditionals.replace(/\{\{(\w+)\}\}/g, (__, key: string) => itemVars[key] ?? '');
        })
        .join('');
    });
    safety++;
  }
  return result;
}

/**
 * Pour les métadonnées de type 'galerie' utilisées "nues" ({{cle}} sans boucle
 * {{#each}} explicite dans le template), génère automatiquement un rendu par
 * défaut (mosaïque ou carousel) en injectant un gabarit qui réutilise les
 * primitives existantes ({{#each}}/{{#if}}/substitution) — pas de moteur de
 * rendu dédié, uniquement du "sucre syntaxique" au niveau du HTML source.
 *
 * ⚠️ Contrepartie mirroir : garder synchronisé avec `injectGalerieAutoRender`
 * dans `backoffice/src/components/templateEditor/TemplatePreview.tsx`.
 */
function injectGalerieAutoRender(html: string, categorie: CategorieMarqueur): string {
  let result = html;
  for (const meta of categorie.metadonnees || []) {
    if (meta.type !== 'galerie') continue;
    const { cle } = meta;
    const hasExplicitEach = new RegExp(`\\{\\{#each\\s+${cle}\\}\\}`).test(result);
    if (hasExplicitEach) continue;

    const bareTokenRegex = new RegExp(`\\{\\{${cle}\\}\\}`, 'g');
    if (!bareTokenRegex.test(result)) continue;

    const mode = meta.modeGalerie ?? 'mosaique';
    const snippet = `<div class="tpl-gallery tpl-gallery--${mode}">{{#each ${cle}}}<figure class="tpl-gallery__item"><img src="{{url}}" alt="{{alt}}">{{#if legende}}<figcaption>{{legende}}</figcaption>{{/if}}</figure>{{/each}}</div>`;
    result = result.replace(bareTokenRegex, snippet);
  }
  return result;
}

/**
 * Remplace les {{variables}} dans le template HTML puis sanitise le résultat.
 * 
 * @param templateHtml - Template HTML brut avec placeholders {{variable}}
 * @param point - Le point à afficher
 * @param categorie - La catégorie du marqueur (contient le template + métadonnées)
 * @returns HTML sanitisé prêt à être injecté avec dangerouslySetInnerHTML
 */
export function renderTemplate(
  templateHtml: string,
  point: Point,
  categorie: CategorieMarqueur
): string {
  if (!templateHtml) return '';

  const { vars, arrays } = buildVariables(point, categorie);

  // Sucre syntaxique : {{cle}} nu pour une métadonnée 'galerie' devient une boucle
  const withAutoRender = injectGalerieAutoRender(templateHtml, categorie);

  // Traitement des boucles {{#each}}...{{/each}}
  const withEach = processEach(withAutoRender, vars, arrays);

  // Traitement des blocs conditionnels {{#if}}...{{/if}}
  const processed = processConditionals(withEach, vars, arrays);

  // Substitution des {{variables}}
  const html = processed.replace(
    /\{\{(\w+)\}\}/g,
    (_, key: string) => vars[key] ?? ''
  );

  // Sanitisation
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}

/**
 * Génère un HTML de fallback (popup par défaut) quand il n'y a pas de template
 */
export function renderFallback(point: Point): string {
  const html = `
    <div class="popup-content">
      <h4>${point.titre || point.nom}</h4>
      ${point.description ? `<p>${point.description.replace(/\n/g, '<br>')}</p>` : ''}
      <div class="popup-meta">
        <span class="category">${point.categorie || ''}</span>
        ${point.latitude !== undefined && point.longitude !== undefined ? `
        <span class="coordinates">
          ${point.latitude.toFixed(4)}, ${point.longitude.toFixed(4)}
        </span>` : ''}
      </div>
    </div>
  `;
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}

/**
 * Génère les CSS Custom Properties (variables) à partir des données du point.
 * Ces variables peuvent être utilisées dans le CSS du template via var(--nomVariable).
 * 
 * Exemple d'utilisation dans le CSS :
 *   .card { background-color: var(--couleur); }
 *   .rating::before { content: var(--notation); }
 * 
 * @param point - Le point contenant les données
 * @param categorie - La catégorie du marqueur
 * @returns Objet style React avec les CSS variables
 */
export function buildCssVariables(
  point: Point,
  categorie: CategorieMarqueur
): React.CSSProperties {
  const { vars } = buildVariables(point, categorie);
  
  // Convertir en CSS variables (--nomVariable: valeur)
  const cssVars: Record<string, string> = {};
  
  for (const [key, value] of Object.entries(vars)) {
    // Les valeurs texte doivent être entre guillemets pour être utilisées avec content:
    // Les couleurs et nombres restent tels quels
    const isColor = /^#[0-9A-Fa-f]{3,8}$|^rgb|^hsl/i.test(value);
    const isNumber = !isNaN(Number(value)) && value !== '';
    
    if (isColor || isNumber) {
      cssVars[`--${key}`] = value;
    } else {
      // Valeur texte : on la met aussi sans guillemets (pour background-image: url(...) par ex.)
      cssVars[`--${key}`] = value;
      // Et une version avec guillemets pour content:
      cssVars[`--${key}-text`] = `"${value.replace(/"/g, '\\"')}"`;
    }
  }
  
  return cssVars as React.CSSProperties;
}

export { SYSTEM_VARIABLES, SANITIZE_CONFIG };
